/**
 * MealPlanSummary Component
 * Reusable component for displaying meal plan information in different formats
 */

import React, { useState, useRef, useEffect } from "react";
import { MealPlan, Profile, mealPlanApi } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { exportToCSV, exportToStyledPDF, exportToJSON } from "../utils/exportUtils";
import { MealPlanData } from "../types/mealPlanning";
import { PrintableMealPlan } from "./PrintableMealPlan";

interface MealPlanSummaryProps {
    mealPlan: MealPlan;
    profiles: Profile[];
    variant?: "compact" | "card" | "detailed" | "list";
    onClick?: () => void;
    onDelete?: () => void;
    onView?: () => void;
    onTemplate?: () => void;
    className?: string;
    style?: React.CSSProperties;
}

export const MealPlanSummary: React.FC<MealPlanSummaryProps> = ({
    mealPlan,
    profiles,
    variant = "card",
    onClick,
    onDelete,
    onView,
    onTemplate,
    className,
    style,
}) => {
    // Get profile names from the meal plan's profile IDs
    const getProfileNames = (): string[] => {
        // Profile IDs are now directly in the profiles field
        const profileIds = mealPlan.profiles || [];

        if (!Array.isArray(profileIds) || profileIds.length === 0) {
            return [];
        }

        return profileIds
            .map((profileId: number) => {
                const profile = profiles.find((p) => p.id === profileId);
                return profile ? profile.name : null;
            })
            .filter((name): name is string => name !== null);
    };

    const profileNames = getProfileNames();
    const createdDate = new Date(mealPlan.created_at);
    const formattedDate = createdDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
    });

    if (variant === "compact") {
        return (
            <CompactMealPlanSummary
                {...{
                    mealPlan,
                    profileNames,
                    formattedDate,
                    onClick,
                    className,
                    style,
                }}
            />
        );
    }

    if (variant === "detailed") {
        return (
            <DetailedMealPlanSummary
                {...{
                    mealPlan,
                    profileNames,
                    formattedDate,
                    onClick,
                    className,
                    style,
                }}
            />
        );
    }

    if (variant === "list") {
        return (
            <ListMealPlanSummary
                {...{
                    mealPlan,
                    profileNames,
                    formattedDate,
                    onClick,
                    onDelete,
                    onView,
                    onTemplate,
                    className,
                    style,
                }}
            />
        );
    }

    // Default card variant
    return (
        <CardMealPlanSummary
            {...{
                mealPlan,
                profileNames,
                formattedDate,
                onClick,
                className,
                style,
            }}
        />
    );
};

interface SummaryProps {
    mealPlan: MealPlan;
    profileNames: string[];
    formattedDate: string;
    onClick?: () => void;
    className?: string;
    style?: React.CSSProperties;
}

// Compact variant - for small spaces, dropdowns, etc.
const CompactMealPlanSummary: React.FC<SummaryProps> = ({
    mealPlan,
    profileNames,
    formattedDate,
    onClick,
    className,
    style,
}) => {
    const containerStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        padding: "8px 12px",
        cursor: onClick ? "pointer" : "default",
        transition: "background-color 0.2s",
        ...style,
    };

    const nameStyle: React.CSSProperties = {
        fontWeight: 600,
        fontSize: "14px",
        color: "#1f2937",
        marginRight: "8px",
        flex: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    };

    const metaStyle: React.CSSProperties = {
        fontSize: "12px",
        color: "#6b7280",
    };

    return (
        <div
            className={className}
            style={containerStyle}
            onClick={onClick}
            onMouseEnter={(e) => {
                if (onClick) {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                }
            }}
            onMouseLeave={(e) => {
                if (onClick) {
                    e.currentTarget.style.backgroundColor = "transparent";
                }
            }}
        >
            <div style={nameStyle} title={mealPlan.name}>
                {mealPlan.name}
            </div>
            <div style={metaStyle}>{formattedDate}</div>
        </div>
    );
};

// Card variant - for lists, grids, etc.
const CardMealPlanSummary: React.FC<SummaryProps> = ({
    mealPlan,
    profileNames,
    formattedDate,
    onClick,
    className,
    style,
}) => {
    const containerStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "8px",
        border: "1px solid #e5e7eb",
        padding: "16px",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        ...style,
    };

    const headerStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: "12px",
    };

    const nameStyle: React.CSSProperties = {
        fontWeight: 600,
        fontSize: "16px",
        color: "#1f2937",
        marginBottom: "4px",
        lineHeight: "1.4",
    };

    const dateStyle: React.CSSProperties = {
        fontSize: "12px",
        color: "#6b7280",
        backgroundColor: "#f3f4f6",
        padding: "2px 8px",
        borderRadius: "4px",
        fontWeight: 500,
    };

    const profilesStyle: React.CSSProperties = {
        fontSize: "12px",
        color: "#6b7280",
    };

    const profileTagStyle: React.CSSProperties = {
        display: "inline-block",
        backgroundColor: "#dbeafe",
        color: "#1e40af",
        padding: "2px 6px",
        borderRadius: "3px",
        marginRight: "4px",
        marginBottom: "2px",
        fontSize: "11px",
        fontWeight: 500,
    };

    return (
        <div
            className={className}
            style={containerStyle}
            onClick={onClick}
            onMouseEnter={(e) => {
                if (onClick) {
                    e.currentTarget.style.boxShadow =
                        "0 4px 6px rgba(0, 0, 0, 0.1)";
                    e.currentTarget.style.borderColor = "#d1d5db";
                }
            }}
            onMouseLeave={(e) => {
                if (onClick) {
                    e.currentTarget.style.boxShadow =
                        "0 1px 3px rgba(0, 0, 0, 0.1)";
                    e.currentTarget.style.borderColor = "#e5e7eb";
                }
            }}
        >
            <div style={headerStyle}>
                <div>
                    <div style={nameStyle}>{mealPlan.name}</div>
                </div>
                <div style={dateStyle}>{formattedDate}</div>
            </div>

            {profileNames.length > 0 && (
                <div style={profilesStyle}>
                    <div style={{ marginBottom: "4px", fontWeight: 500 }}>
                        Profiles used:
                    </div>
                    <div>
                        {profileNames.map((name, index) => (
                            <span key={index} style={profileTagStyle}>
                                {name}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Detailed variant - for full view, modals, etc.
const DetailedMealPlanSummary: React.FC<SummaryProps> = ({
    mealPlan,
    profileNames,
    formattedDate,
    onClick,
    className,
    style,
}) => {
    const containerStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        padding: "24px",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        ...style,
    };

    const headerStyle: React.CSSProperties = {
        marginBottom: "16px",
        paddingBottom: "16px",
        borderBottom: "1px solid #f3f4f6",
    };

    const nameStyle: React.CSSProperties = {
        fontWeight: 700,
        fontSize: "20px",
        color: "#1f2937",
        marginBottom: "8px",
        lineHeight: "1.3",
    };

    const metaRowStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: "16px",
        fontSize: "14px",
        color: "#6b7280",
    };

    const dateStyle: React.CSSProperties = {
        backgroundColor: "#f3f4f6",
        padding: "4px 12px",
        borderRadius: "6px",
        fontWeight: 500,
    };

    const profilesSectionStyle: React.CSSProperties = {
        marginTop: "16px",
    };

    const profilesLabelStyle: React.CSSProperties = {
        fontSize: "14px",
        fontWeight: 600,
        color: "#374151",
        marginBottom: "8px",
    };

    const profileTagStyle: React.CSSProperties = {
        display: "inline-block",
        backgroundColor: "#dbeafe",
        color: "#1e40af",
        padding: "6px 12px",
        borderRadius: "6px",
        marginRight: "8px",
        marginBottom: "4px",
        fontSize: "13px",
        fontWeight: 500,
    };

    return (
        <div
            className={className}
            style={containerStyle}
            onClick={onClick}
            onMouseEnter={(e) => {
                if (onClick) {
                    e.currentTarget.style.boxShadow =
                        "0 8px 25px rgba(0, 0, 0, 0.15)";
                    e.currentTarget.style.borderColor = "#d1d5db";
                }
            }}
            onMouseLeave={(e) => {
                if (onClick) {
                    e.currentTarget.style.boxShadow =
                        "0 4px 6px rgba(0, 0, 0, 0.1)";
                    e.currentTarget.style.borderColor = "#e5e7eb";
                }
            }}
        >
            <div style={headerStyle}>
                <div style={nameStyle}>{mealPlan.name}</div>
                <div style={metaRowStyle}>
                    <span>Created</span>
                    <span style={dateStyle}>{formattedDate}</span>
                </div>
            </div>

            {profileNames.length > 0 && (
                <div style={profilesSectionStyle}>
                    <div style={profilesLabelStyle}>
                        Generated for {profileNames.length} profile
                        {profileNames.length !== 1 ? "s" : ""}:
                    </div>
                    <div>
                        {profileNames.map((name, index) => (
                            <span key={index} style={profileTagStyle}>
                                👤 {name}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// List variant - for row-based layouts with actions
const ListMealPlanSummary: React.FC<
    SummaryProps & {
        onDelete?: () => void;
        onView?: () => void;
        onTemplate?: () => void;
    }
> = ({
    mealPlan,
    profileNames,
    formattedDate,
    onClick,
    onDelete,
    onView,
    onTemplate,
    className,
    style,
}) => {
    const { dbUser } = useAuth();
    const [showDownloadMenu, setShowDownloadMenu] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [showPrintableView, setShowPrintableView] = useState(false);
    const [mealPlanDataForPrint, setMealPlanDataForPrint] = useState<MealPlanData | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Add CSS animation for spinner
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDownloadMenu(false);
            }
        };

        if (showDownloadMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDownloadMenu]);

    const handleDownload = async (format: 'json' | 'csv' | 'pdf') => {
        if (!dbUser?.id || downloading) return;

        try {
            setDownloading(true);
            
            // Fetch the full meal plan data
            const fullMealPlan = await mealPlanApi.getMealPlan(dbUser.id, mealPlan.id);
            const mealPlanData = fullMealPlan.plan as MealPlanData;

            // Export based on selected format
            switch (format) {
                case 'json':
                    await exportToJSON(mealPlanData);
                    break;
                case 'csv':
                    await exportToCSV(mealPlanData);
                    break;
                case 'pdf':
                    await exportToStyledPDF(mealPlanData);
                    break;
            }

            setShowDownloadMenu(false);
        } catch (error) {
            console.error('Error downloading meal plan:', error);
            // TODO: Show error notification
        } finally {
            setDownloading(false);
        }
    };

    const handlePrintableView = async () => {
        if (!dbUser?.id || downloading) return;

        try {
            setDownloading(true);
            
            // Fetch the full meal plan data
            const fullMealPlan = await mealPlanApi.getMealPlan(dbUser.id, mealPlan.id);
            const mealPlanData = fullMealPlan.plan as MealPlanData;
            
            setMealPlanDataForPrint(mealPlanData);
            setShowPrintableView(true);
            setShowDownloadMenu(false);
        } catch (error) {
            console.error('Error loading meal plan for printing:', error);
            // TODO: Show error notification
        } finally {
            setDownloading(false);
        }
    };

    const containerStyle: React.CSSProperties = {
        backgroundColor: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        padding: "16px 20px",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s",
        display: "flex",
        alignItems: "center",
        gap: "16px",
        marginBottom: "8px",
        ...style,
    };

    const contentStyle: React.CSSProperties = {
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: "16px",
    };

    const nameStyle: React.CSSProperties = {
        fontWeight: 600,
        fontSize: "16px",
        color: "#1f2937",
        minWidth: "200px",
        lineHeight: "1.4",
    };

    const profilesStyle: React.CSSProperties = {
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: "6px",
        flexWrap: "wrap",
    };

    const profileTagStyle: React.CSSProperties = {
        display: "inline-block",
        backgroundColor: "#dbeafe",
        color: "#1e40af",
        padding: "2px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: 500,
    };

    const dateStyle: React.CSSProperties = {
        fontSize: "13px",
        color: "#6b7280",
        backgroundColor: "#f3f4f6",
        padding: "4px 8px",
        borderRadius: "4px",
        fontWeight: 500,
        minWidth: "80px",
        textAlign: "center",
    };

    const tokenStyle: React.CSSProperties = {
        fontSize: "12px",
        color: "#7c3aed",
        backgroundColor: "#f3e8ff",
        padding: "4px 8px",
        borderRadius: "4px",
        fontWeight: 500,
        minWidth: "60px",
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        gap: "4px",
    };

    const actionsStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginLeft: "auto",
    };

    const actionButtonStyle: React.CSSProperties = {
        width: "36px",
        height: "36px",
        borderRadius: "6px",
        border: "1px solid #d1d5db",
        backgroundColor: "white",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.2s",
        fontSize: "16px",
    };

    const deleteButtonStyle: React.CSSProperties = {
        ...actionButtonStyle,
        color: "#dc2626",
    };

    const viewButtonStyle: React.CSSProperties = {
        ...actionButtonStyle,
        color: "#3b82f6",
    };

    const templateButtonStyle: React.CSSProperties = {
        ...actionButtonStyle,
        color: "#059669",
    };

    const downloadButtonStyle: React.CSSProperties = {
        ...actionButtonStyle,
        color: "#7c3aed",
        position: "relative",
    };

    const dropdownStyle: React.CSSProperties = {
        position: "absolute",
        top: "100%",
        right: 0,
        marginTop: "4px",
        backgroundColor: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "6px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        minWidth: "120px",
        zIndex: 10,
        overflow: "hidden",
    };

    const dropdownItemStyle: React.CSSProperties = {
        padding: "8px 12px",
        fontSize: "14px",
        color: "#374151",
        cursor: downloading ? "not-allowed" : "pointer",
        borderBottom: "1px solid #f3f4f6",
        transition: "background-color 0.2s",
        opacity: downloading ? 0.6 : 1,
    };

    return (
        <>
        <div
            className={className}
            style={containerStyle}
            onClick={onClick}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow =
                    "0 2px 4px rgba(0, 0, 0, 0.1)";
                e.currentTarget.style.borderColor = "#d1d5db";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.borderColor = "#e5e7eb";
            }}
        >
            <div style={contentStyle}>
                <div style={nameStyle}>{mealPlan.name}</div>

                <div style={profilesStyle}>
                    {profileNames.length > 0 ? (
                        profileNames.map((name, index) => (
                            <span key={index} style={profileTagStyle}>
                                {name}
                            </span>
                        ))
                    ) : (
                        <span style={{ color: "#9ca3af", fontSize: "13px" }}>
                            No profiles
                        </span>
                    )}
                </div>

                <div style={dateStyle}>{formattedDate}</div>

                {mealPlan.token_count !== null &&
                    mealPlan.token_count !== undefined && (
                        <div
                            style={tokenStyle}
                            title={`${mealPlan.token_count.toLocaleString()} tokens used`}
                        >
                            <span>🪙</span>
                            <span>
                                {mealPlan.token_count >= 1000
                                    ? `${(mealPlan.token_count / 1000).toFixed(
                                          1
                                      )}k`
                                    : mealPlan.token_count}
                            </span>
                        </div>
                    )}
            </div>

            <div style={actionsStyle}>
                <div style={{ position: "relative" }} ref={dropdownRef}>
                    <button
                        style={downloadButtonStyle}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowDownloadMenu(!showDownloadMenu);
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f3e8ff";
                            e.currentTarget.style.borderColor = "#7c3aed";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "white";
                            e.currentTarget.style.borderColor = "#d1d5db";
                        }}
                        title="Download meal plan"
                        disabled={downloading}
                    >
                        {downloading ? (
                            <svg 
                                width="18" 
                                height="18" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2"
                                style={{
                                    animation: "spin 1s linear infinite"
                                }}
                            >
                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                            </svg>
                        ) : (
                            <svg 
                                width="18" 
                                height="18" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                            >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                        )}
                    </button>

                    {showDownloadMenu && (
                        <div style={dropdownStyle}>
                            <div
                                style={{ ...dropdownItemStyle, borderBottom: "1px solid #f3f4f6" }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePrintableView();
                                }}
                                onMouseEnter={(e) => {
                                    if (!downloading) {
                                        e.currentTarget.style.backgroundColor = "#f9fafb";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "white";
                                }}
                            >
                                <span style={{ marginRight: "8px" }}>🖨️</span> Print View
                            </div>
                            <div
                                style={{ ...dropdownItemStyle, borderBottom: "1px solid #f3f4f6" }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload('json');
                                }}
                                onMouseEnter={(e) => {
                                    if (!downloading) {
                                        e.currentTarget.style.backgroundColor = "#f9fafb";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "white";
                                }}
                            >
                                <span style={{ marginRight: "8px" }}>📋</span> As JSON
                            </div>
                            <div
                                style={{ ...dropdownItemStyle, borderBottom: "1px solid #f3f4f6" }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload('csv');
                                }}
                                onMouseEnter={(e) => {
                                    if (!downloading) {
                                        e.currentTarget.style.backgroundColor = "#f9fafb";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "white";
                                }}
                            >
                                <span style={{ marginRight: "8px" }}>📊</span> As CSV
                            </div>
                            <div
                                style={{ ...dropdownItemStyle, borderBottom: "none" }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload('pdf');
                                }}
                                onMouseEnter={(e) => {
                                    if (!downloading) {
                                        e.currentTarget.style.backgroundColor = "#f9fafb";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "white";
                                }}
                            >
                                <span style={{ marginRight: "8px" }}>📄</span> As PDF
                            </div>
                        </div>
                    )}
                </div>

                <button
                    style={viewButtonStyle}
                    onClick={(e) => {
                        e.stopPropagation();
                        onClick?.(); // Use the same action as clicking the card
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#eff6ff";
                        e.currentTarget.style.borderColor = "#3b82f6";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "white";
                        e.currentTarget.style.borderColor = "#d1d5db";
                    }}
                    title="View/Edit meal plan"
                >
                    👁️
                </button>

                <button
                    style={templateButtonStyle}
                    onClick={(e) => {
                        e.stopPropagation();
                        onTemplate?.();
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#ecfdf5";
                        e.currentTarget.style.borderColor = "#059669";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "white";
                        e.currentTarget.style.borderColor = "#d1d5db";
                    }}
                    title="Use as template"
                >
                    📋
                </button>

                <button
                    style={deleteButtonStyle}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.();
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#fef2f2";
                        e.currentTarget.style.borderColor = "#dc2626";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "white";
                        e.currentTarget.style.borderColor = "#d1d5db";
                    }}
                    title="Delete meal plan"
                >
                    🗑️
                </button>
            </div>
        </div>

        {/* Printable View Modal */}
        {showPrintableView && mealPlanDataForPrint && (
            <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px",
            }}>
                <div style={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    width: "90vw",
                    height: "90vh",
                    overflow: "auto",
                    padding: "20px",
                    position: "relative",
                }}>
                    {mealPlanDataForPrint && (
                        <PrintableMealPlan
                            mealPlanData={mealPlanDataForPrint}
                            onClose={() => {
                                setShowPrintableView(false);
                                setMealPlanDataForPrint(null);
                            }}
                        />
                    )}
                </div>
            </div>
        )}
    </>
    );
};
